// src/pages/SystemConfiguration/components/LookupConfigForm.tsx
// Form component for Lookup Configuration with URL validation

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Row, Col, Label, Input, FormFeedback, Button, Alert, Badge } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { SystemConfiguration, LookupConfigData } from '../types';
import { setValidationResult } from 'slices/systemConfiguration/reducer';
import { updateLookupConfig, testLookupUrl } from 'slices/systemConfiguration/thunk';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('system_configuration');

interface LookupConfigFormProps {
  config: SystemConfiguration | null;
  loading: boolean;
  onSubmit?: (values: any) => void;
  onReset?: () => void;
  formikRef?: React.RefObject<any>;
}

const LookupConfigForm: React.FC<LookupConfigFormProps> = ({ config, loading, onSubmit, onReset, formikRef }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const [validating, setValidating] = useState(false);
  const [urlStatus, setUrlStatus] = useState<'valid' | 'invalid' | null>(null);
  
  // Validation schema
  const validationSchema = Yup.object({
    lookupBaseUrl: Yup.string()
      .required(st('vui_long_nhap_base_url_tra_error'))
      .url(st('url_khong_hop_le_error'))
      .matches(/^https?:\/\/.+\/$/, st('url_phai_ket_thuc_bang_error'))
      .test('no-query-params', st('url_khong_duoc_chua_query_parameters_error'), (value) => {
        return !value?.includes('?');
      }),
    returnTicketStartTime: Yup.string()
      .required(st('vui_long_nhap_gio_bat_dau_error'))
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/, st('dinh_dang_gio_khong_hop_le_error')),
    returnTicketEndTime: Yup.string()
      .required(st('vui_long_nhap_gio_ket_thuc_error'))
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/, st('dinh_dang_gio_khong_hop_le_error'))
      .test('after-start', st('gio_ket_thuc_phai_sau_gio_error'), function(value) {
        const { returnTicketStartTime } = this.parent;
        if (!value || !returnTicketStartTime) return true;
        return value > returnTicketStartTime;
      })
      // Đồng bộ với rule tối thiểu 60 phút phía server (LookupValidation.ValidateTimeWindow,
      // system-service) — chặn ngay ở client, tránh phải chờ submit mới biết lỗi.
      .test('min-60-minutes', st('khung_gio_nhan_ve_e_toi_error'), function(value) {
        const { returnTicketStartTime } = this.parent;
        if (!value || !returnTicketStartTime || value <= returnTicketStartTime) return true;
        const toMinutes = (t: string) => {
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
        };
        return toMinutes(value) - toMinutes(returnTicketStartTime) >= 60;
      }),
  });
  
    const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      lookupBaseUrl: config?.lookupBaseUrl || '',
      returnTicketStartTime: config?.returnTicketStartTime || '16:00:00',
      returnTicketEndTime: config?.returnTicketEndTime || '17:30:00',
    },
    validationSchema,
    onSubmit: onSubmit || (async (values) => {
      const result = await dispatch(updateLookupConfig({
        lookupBaseUrl: values.lookupBaseUrl,
        returnTicketStartTime: values.returnTicketStartTime,
        returnTicketEndTime: values.returnTicketEndTime,
      }));

      if (updateLookupConfig.fulfilled.match(result)) {
        toast.success(st('da_luu_cau_hinh_duong_dan_message'));
      } else {
        toast.error((result.payload as string) || st('khong_the_luu_cau_hinh_duong_message'));
      }
    }),
  });
  
  // Expose formik instance via ref
  React.useImperativeHandle(formikRef, () => ({
    submitForm: validation.submitForm,
    resetForm: onReset || validation.resetForm,
  }));
  
  const handleValidateUrl = async () => {
    const url = validation.values.lookupBaseUrl;
    
    if (!url) {
      toast.error(st('vui_long_nhap_url_truoc_message'));
      return;
    }
    
    setValidating(true);
    setUrlStatus(null);

    try {
      const result: any = await dispatch(testLookupUrl(url)).unwrap();

      setUrlStatus(result.isValid && result.isAccessible ? 'valid' : 'invalid');
      dispatch(setValidationResult(result));

      if (result.isValid && result.isAccessible) {
        toast.success(st('url_hop_le_va_co_the_message'));
      } else {
        toast.error(result.errorMessage || st('url_khong_hop_le_hoac_khong_message'));
      }
    } catch (error: any) {
      setUrlStatus('invalid');
      dispatch(setValidationResult({
        isValid: false,
        isAccessible: false,
        errorMessage: error?.message || error || st('khong_the_kiem_tra_duong_dan_text'),
      }));
      toast.error(typeof error === 'string' ? error : st('khong_the_kiem_tra_duong_dan_message'));
    } finally {
      setValidating(false);
    }
  };
  
  if (loading && !config) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{st('dang_tai_text')}</span>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={validation.handleSubmit}>
      <Alert color="info" className="mb-3">
        <i className="ri-information-line me-2"></i>
        <strong>{st('dinh_dang_url_text')}</strong> {st('phai_bat_dau_bang_http_hoac_message')}
      </Alert>
      
      <Row>
        <Col md={12}>
          <div className="mb-3">
            <Label htmlFor="lookupBaseUrl" className="form-label">
              {st('base_url_tra_cuu_label')} <span className="text-danger">*</span>
            </Label>
            <div className="input-group">
              <Input
                type="text"
                id="lookupBaseUrl"
                name="lookupBaseUrl"
                className="form-control"
                placeholder={st('https_xoso_com_vn_getkqxs_placeholder')}
                value={validation.values.lookupBaseUrl}
                data-action-code="input-lookup-base-url"
                data-action-name="Base URL tra cứu"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-lookup-base-url')}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={validation.touched.lookupBaseUrl && validation.errors.lookupBaseUrl ? true : false}
              />
              {PageActionPermission.canDisplay(location.pathname, 'button-validate-url') && (
              <Button
                type="button"
                color="primary"
                outline
                data-action-code="button-validate-url"
                data-action-name="Kiểm tra URL"
                onClick={handleValidateUrl}
                disabled={validating || !validation.values.lookupBaseUrl}
              >
                {validating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {st('dang_kiem_tra_text')}
                  </>
                ) : (
                  <>
                    <i className="ri-check-double-line me-1"></i>
                    {st('kiem_tra_url_text')}
                  </>
                )}
              </Button>
              )}
            </div>
            {validation.touched.lookupBaseUrl && validation.errors.lookupBaseUrl && (
              <FormFeedback type="invalid" className="d-block">{validation.errors.lookupBaseUrl}</FormFeedback>
            )}
            {urlStatus && (
              <div className="mt-2">
                <Badge color={urlStatus === 'valid' ? 'success' : 'danger'}>
                  {urlStatus === 'valid' ? (
                    <><i className="ri-checkbox-circle-line me-1"></i>{st('url_hop_le_va_co_the_message')}</>
                  ) : (
                    <><i className="ri-close-circle-line me-1"></i>{st('kiem_tra_url_that_bai_text')}</>
                  )}
                </Badge>
              </div>
            )}
          </div>
        </Col>
      </Row>
      
      <Row>
        <Col md={12}>
          <Label className="form-label">
            {st('khung_gio_nhan_ve_e_label')}  <span className="text-danger">*</span>
          </Label>
        </Col>
      </Row>
      
      <Row>
        <Col md={6}>
          <div className="mb-3">
            <Label htmlFor="returnTicketStartTime" className="form-label">
              {st('gio_bat_dau_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="time"
              id="returnTicketStartTime"
              name="returnTicketStartTime"
              className="form-control"
              step="1"
              value={validation.values.returnTicketStartTime}
              data-action-code="input-return-ticket-start-time"
              data-action-name="Giờ bắt đầu nhận vé ế"
              disabled={!PageActionPermission.canEnable(location.pathname, 'input-return-ticket-start-time')}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.returnTicketStartTime && validation.errors.returnTicketStartTime ? true : false}
            />
            {validation.touched.returnTicketStartTime && validation.errors.returnTicketStartTime && (
              <FormFeedback type="invalid">{validation.errors.returnTicketStartTime}</FormFeedback>
            )}
          </div>
        </Col>
        
        <Col md={6}>
          <div className="mb-3">
            <Label htmlFor="returnTicketEndTime" className="form-label">
              {st('gio_ket_thuc_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="time"
              id="returnTicketEndTime"
              name="returnTicketEndTime"
              className="form-control"
              step="1"
              value={validation.values.returnTicketEndTime}
              data-action-code="input-return-ticket-end-time"
              data-action-name="Giờ kết thúc nhận vé ế"
              disabled={!PageActionPermission.canEnable(location.pathname, 'input-return-ticket-end-time')}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.returnTicketEndTime && validation.errors.returnTicketEndTime ? true : false}
            />
            {validation.touched.returnTicketEndTime && validation.errors.returnTicketEndTime && (
              <FormFeedback type="invalid">{validation.errors.returnTicketEndTime}</FormFeedback>
            )}
          </div>
        </Col>
      </Row>
      
      <Alert color="warning" className="mb-3">
        <i className="ri-time-line me-2"></i>
        <strong>{st('luu_y_quan_trong_text')}</strong> {st('khung_gio_nhan_ve_e_toi_message')}
      </Alert>
      
    </form>
  );
};

export default LookupConfigForm;